import { lazy, Suspense, useRef } from "react";
import { Box, BoxProps } from "@mui/material";
import { IWikifolioDetailDict } from "src/api/server/wf-detail.types";
import { useIsSectionInView } from "src/components-mui/common/jump-marks/use-is-section-in-view";
import { CHART_VARIANTS } from "./shared/chart.type";
import { ChartLoadingSkeleton } from "./shared/components/chart-loading-skeleton";
import { ChipSkeleton } from "./shared/components/chip-skeleton";
import { PortfolioDistributionShell } from "./shared/portfolio-distribution-shell";

const PORTFOLIO_DISTRIBUTION_SECTION_TEST_ID = "portfolio-distribution-section";

// Module level on purpose: a lazy component created during render would remount the charts on every re-render.
const PortfolioDistributionCharts = lazy(() =>
    import("./portfolio-distribution-charts").then(component => ({ default: component.PortfolioDistributionCharts }))
);

const PortfolioDistributionSectionSkeleton = ({ dict }: { dict: IWikifolioDetailDict["portfolioDistribution"] }) => {
    return (
        <PortfolioDistributionShell
            dict={dict}
            Chip={<ChipSkeleton />}
            PieChart={<ChartLoadingSkeleton />}
            MapChart={<ChartLoadingSkeleton variant={CHART_VARIANTS.MAP} />}
        />
    );
};

interface IPortfolioDistributionLazyProps extends BoxProps {
    dict: IWikifolioDetailDict["portfolioDistribution"];
    wikifolioId: string;
}

export const PortfolioDistributionSection = ({ dict, wikifolioId, ...boxProps }: IPortfolioDistributionLazyProps) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const isVisible = useIsSectionInView({ sectionRef, threshold: 0.5, visibleOnce: true });
    const skeleton = <PortfolioDistributionSectionSkeleton dict={dict} />;

    return (
        <Box component="section" ref={sectionRef} data-test-id={PORTFOLIO_DISTRIBUTION_SECTION_TEST_ID} {...boxProps}>
            {isVisible ? (
                <Suspense fallback={skeleton}>
                    <PortfolioDistributionCharts dict={dict} wikifolioId={wikifolioId} />
                </Suspense>
            ) : (
                skeleton
            )}
        </Box>
    );
};
